import { useEffect, useRef, type MutableRefObject } from 'react';
import type { DraftContext } from '@/features/coach-review/composerDrafts';
import { FRAME, MAX_CLIP_DURATION_SECONDS, clipSeverity, formatDuration, formatEditorTime,
  type ClipWindow } from '@/features/coach-review/clipTrack';
import { CLIP_PRESETS, clipInAt, clipOutAt, clipPreset, boundClipWindow } from './resourceClipWindow';
import type { ResourceVideoSession, ResourceVideoValue } from './resourceVideoSession';
import { ResourceVideoStage, type ResourceStagePosition } from './ResourceVideoStage';
import { shortcutBlocked } from './resourceShortcuts';

type VideoState = Extract<ReturnType<ResourceVideoSession['getState']>, { status:'available' }>;
export function ResourceStageClip({ session, state, context, editable, active, focus, matchName, position }:{
  session:ResourceVideoSession; state:VideoState; context:DraftContext; editable:boolean; active:boolean; focus:boolean;
  matchName:string; position:MutableRefObject<ResourceStagePosition>;
}) {
  const value = state.value as ResourceVideoValue;
  const apply = (window:ClipWindow | ((current:ClipWindow) => ClipWindow)) => {
    const current = session.getState();
    if (current.status !== 'available' || !current.canEdit) return;
    const clip = current.value as ResourceVideoValue;
    const next = typeof window === 'function' ? window(clip.window)
      : boundClipWindow(window, clip.window, position.current.duration);
    if (next.start !== clip.window.start || next.end !== clip.window.end) session.change({ ...clip, window:next });
  };
  const keyed = useRef(apply); keyed.current = apply;
  useEffect(() => {
    if (!editable) return;
    const keys = (event:KeyboardEvent) => {
      if (shortcutBlocked(event, active)) return;
      const { playhead, duration } = position.current, key = event.key.toLowerCase();
      if (key === 'i') keyed.current(window => clipInAt(window, playhead, duration));
      else if (key === 'o') keyed.current(window => clipOutAt(window, playhead, duration));
    };
    window.addEventListener('keydown', keys);
    return () => window.removeEventListener('keydown', keys);
  }, [editable, active, position]);
  const length = Math.max(0, value.window.end - value.window.start);
  const severity = clipSeverity(value.window);
  const atLimit = length >= MAX_CLIP_DURATION_SECONDS - FRAME / 2;
  return <ResourceVideoStage session={session} state={state} context={context} editable={editable} active={active} focus={focus}
    matchName={matchName} anchor={value.window.start} position={position}
    clip={{ window:value.window, onChange:apply }}
    // A clip seeded before the length was known stays inside the recording.
    onDuration={duration => apply(window => boundClipWindow(window, window, duration))}
    readout={() => <span className="flex items-center gap-2" data-testid="resource-clip-readout">
      <span className={`text-xs ${severity === 'ok' ? '' : 'text-flag'}`}>{atLimit ? 'At the 120 s limit' : severity === 'warn' ? 'Near the 120 s limit' : 'Clip length · up to 120 s'}</span>
    </span>}
    footer={(_time, duration) => <div className="editor-clip-row">
      <div className="editor-clip-range" data-testid="clip-track-readout">
        <span><b>IN</b> {formatEditorTime(value.window.start)}</span>
        <span><b>OUT</b> {formatEditorTime(value.window.end)}</span>
        <span className="editor-duration"><strong data-testid="resource-clip-duration" className={severity === 'ok' ? '' : 'text-flag'}>{formatDuration(length)}</strong> selected</span>
      </div>
      <fieldset disabled={!editable || duration <= 0} className="editor-clip-actions">
        <div className="editor-clip-actions-core">
          <button type="button" className="editor-button" onClick={() => apply(window => clipInAt(window, position.current.playhead, duration))}>Set IN here</button>
          <button type="button" className="editor-button" onClick={() => apply(window => clipOutAt(window, position.current.playhead, duration))}>Set OUT here</button>
          <div className="editor-presets" role="group" aria-label="Length from the in-point">
            {CLIP_PRESETS.map(seconds => <button key={seconds} type="button" className="editor-button" data-testid={`resource-clip-preset-${seconds}`}
              aria-pressed={Math.round(length) === seconds} aria-label={`Set clip to ${seconds} seconds`}
              onClick={() => apply(window => clipPreset(window, seconds, duration))}>{seconds}s</button>)}
          </div>
        </div>
        <span className="editor-waiting">Keys: I sets in, O sets out at the playhead</span>
      </fieldset>
    </div>}/>;
}
